import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { appTableFeatures, TableCell } from '@design/components/table';
import { isElementScrollbarVisible, useScrollListener } from '@helpers';
import { PaginationState, useTable } from '@tanstack/react-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { SortableTableHeaderCell } from '@terminal/components/sortable-table-header-cell/SortableTableHeaderCell';
import {
  getEmptyColumns,
  SCENARIO_ENTITY_COLUMN_ID,
} from '@terminal/widgets/scenario/utils/scenario-table-utils/scenario-table-utils';
import clsx from 'clsx';
import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EmptyTable.module.scss';
import { useEmptyTableActions } from './hooks/useEmptyTableActions';

export interface EmptyTableProps {
  entityTags?: string[];
  children?: ReactNode;
}

const PAGE_SIZE = 20;

export const EmptyTable = ({ entityTags, children }: EmptyTableProps) => {
  const { t } = useTranslation('scenario');

  const data = useMemo(
    () => (entityTags ? entityTags.map(entityTag => ({ entityTag })) : []),
    [entityTags]
  );

  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const columns = useMemo(() => getEmptyColumns(t), [t]);
  const table = useTable({
    features: appTableFeatures,
    data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  useScrollListener(
    containerRef,
    () => {
      if (table.getCanNextPage()) {
        table.setPageSize(pagination.pageSize + PAGE_SIZE);
      }

      return false;
    },
    [table, pagination],
    { isGlobalScrollListened: false, offset: 45 }
  );

  useLayoutEffect(() => {
    const hasScroll = isElementScrollbarVisible(containerRef, 'vertical');

    if (hasScroll || !containerRef.current) {
      return;
    }

    if (table.getCanNextPage()) {
      table.setPageSize(pagination.pageSize + PAGE_SIZE);
    }
  }, [containerRef, pagination.pageSize]);

  const { getEnabledKeys, handleDropdownAction } = useEmptyTableActions();

  const { rows } = table.getRowModel();

  return (
    <div
      ref={containerRef}
      className={clsx(styles.emptyTableRoot, {
        [styles.notEmpty]: rows.length > 0,
      })}
    >
      <table className={styles.emptyTable} data-testid="scenario-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <SortableTableHeaderCell
                  key={header.id}
                  header={header}
                  handleAction={handleDropdownAction(header.column)}
                  enabledKeys={getEnabledKeys(header.column)}
                  enableDropdown={
                    !!data.length && !!getEnabledKeys(header.column).length
                  }
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id}>
              {row
                .getVisibleCells()
                .filter(row => row.column.id === SCENARIO_ENTITY_COLUMN_ID)
                .map(cell => {
                  return (
                    <TableCell key={cell.id} isNumber={false}>
                      <EntityCell entityTag={row.original.entityTag} />
                    </TableCell>
                  );
                })}

              {index === 0 && (
                <td
                  className={styles.emptyCell}
                  data-testid="empty-cell"
                  colSpan={columns.length - 1}
                  rowSpan={rows.length}
                >
                  <div
                    className={
                      rows.length > PAGE_SIZE
                        ? styles.stickyContent
                        : styles.absoluteContent
                    }
                  >
                    {children}
                  </div>
                </td>
              )}
            </tr>
          ))}

          {table.getCanNextPage() && (
            <tr data-testid="loading-row">
              {rows[0].getVisibleCells().map(cell => (
                <TableCell key={cell.id} isNumber={false}>
                  <SkeletonLoader
                    areas={[
                      {
                        styles: {
                          width: '100%',
                          height: '20px',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                </TableCell>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
